import type {
  Contact,
  ContactCreate,
  ContactStatus,
  ContactUpdate,
  CrmAccount,
} from '@erp/shared';
import { Ban, Pencil, RotateCcw, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, PhoneInput } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const STATUS_VARIANT: Record<ContactStatus, 'success' | 'secondary'> = {
  active: 'success',
  inactive: 'secondary',
};

export function ContactsPage(): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('crm.contacts.write');
  const [items, setItems] = useState<Contact[] | null>(null);
  const [accounts, setAccounts] = useState<CrmAccount[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContactStatus | ''>('active');

  const accountNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) m.set(a.id, a.name);
    return m;
  }, [accounts]);

  const reload = async (status: ContactStatus | '' = statusFilter): Promise<void> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    const url = qs.length > 0 ? `/crm/contacts?${qs}` : '/crm/contacts';
    const r = await api().get<{ items: Contact[] }>(url);
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    void (async () => {
      const r = await api().get<{ items: CrmAccount[] }>('/crm/accounts?status=active');
      setAccounts(r.data.items);
    })();
  }, []);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const openEdit = (c: Contact): void => {
    setShowCreate(false);
    setEditing(c);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeactivate = async (c: Contact): Promise<void> => {
    if (!(await confirmDialog({ message: t('contacts.actions.confirmDeactivate', { name: c.displayName }), tone: 'danger' }))) return;
    try {
      await api().delete(`/crm/contacts/${c.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (c: Contact): Promise<void> => {
    if (!(await confirmDialog({ message: t('contacts.actions.confirmReactivate', { name: c.displayName }) }))) return;
    try {
      await api().patch(`/crm/contacts/${c.id}`, { status: 'active' });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<Contact>[] = [
    {
      id: 'name',
      header: t('contacts.columns.name'),
      sortValue: (c) => c.displayName,
      searchValue: (c) => c.displayName,
      cellClassName: 'font-medium',
      cell: (c) => c.displayName,
    },
    {
      id: 'title',
      header: t('contacts.columns.title'),
      sortValue: (c) => c.title ?? '',
      searchValue: (c) => c.title ?? '',
      cellClassName: 'text-fg-muted',
      cell: (c) => c.title ?? '—',
    },
    {
      id: 'account',
      header: t('contacts.columns.account'),
      sortValue: (c) => (c.accountId ? accountNameById.get(c.accountId) ?? '' : ''),
      searchValue: (c) => (c.accountId ? accountNameById.get(c.accountId) ?? '' : ''),
      cellClassName: 'text-fg-muted',
      cell: (c) => (c.accountId ? accountNameById.get(c.accountId) ?? '—' : '—'),
    },
    {
      id: 'email',
      header: t('contacts.columns.email'),
      sortValue: (c) => c.email ?? '',
      searchValue: (c) => c.email ?? '',
      cellClassName: 'text-fg-muted',
      cell: (c) => c.email ?? '—',
    },
    {
      id: 'phone',
      header: t('contacts.columns.phone'),
      sortValue: (c) => c.phone ?? '',
      searchValue: (c) => c.phone ?? '',
      cellClassName: 'font-mono text-xs',
      cell: (c) => c.phone ?? '—',
    },
    {
      id: 'status',
      header: t('contacts.columns.status'),
      sortValue: (c) => t(`contacts.status.${c.status}`),
      searchValue: (c) => t(`contacts.status.${c.status}`),
      cell: (c) => <Badge variant={STATUS_VARIANT[c.status]}>{t(`contacts.status.${c.status}`)}</Badge>,
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-44',
            cell: (c: Contact) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(c)}
                  aria-label={`${t('contacts.actions.edit')} ${c.displayName}`}
                  title={t('contacts.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {c.status === 'active' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeactivate(c)}
                    aria-label={`${t('contacts.actions.deactivate')} ${c.displayName}`}
                    title={t('contacts.actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReactivate(c)}
                    aria-label={`${t('contacts.actions.reactivate')} ${c.displayName}`}
                    title={t('contacts.actions.reactivate')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('contacts.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('contacts.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('contacts.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreateContactForm
          accounts={accounts}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditContactForm
          contact={editing}
          accounts={accounts}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ContactStatus | '')}
          aria-label={t('contacts.statusFilter')}
        >
          <option value="">{t('contacts.allStatuses')}</option>
          <option value="active">{t('contacts.status.active')}</option>
          <option value="inactive">{t('contacts.status.inactive')}</option>
        </select>
      </div>

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Users className="h-8 w-8" aria-hidden />}
          title={t('contacts.empty')}
          description={t('contacts.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('contacts.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(c) => c.id}
            searchPlaceholder={t('contacts.searchPlaceholder')}
          />
        </Card>
      )}
    </div>
  );
}

interface CreateFormProps {
  accounts: CrmAccount[];
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}

function CreateContactForm({ accounts, onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<ContactCreate>({ firstName: '', lastName: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/crm/contacts', form);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('contacts.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="c-first" label={t('contacts.create.firstName')} required>
          <Input id="c-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="c-last" label={t('contacts.create.lastName')} required>
          <Input id="c-last" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
        </FormField>
        <FormField id="c-title" label={t('contacts.create.jobTitle')}>
          <Input id="c-title" value={form.title ?? ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </FormField>
        <FormField id="c-account" label={t('contacts.create.account')}>
          <select
            id="c-account"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.accountId ?? ''}
            onChange={(e) => setForm({ ...form, accountId: e.target.value || null })}
          >
            <option value="">{t('contacts.create.noAccount')}</option>
            {accounts.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
          </select>
        </FormField>
        <FormField id="c-email" label={t('contacts.create.email')}>
          <Input id="c-email" type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>
        <FormField id="c-phone" label={t('contacts.create.phone')}>
          <PhoneInput id="c-phone" value={form.phone ?? ''} onChange={(v) => setForm({ ...form, phone: v })} />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditContactFormProps {
  contact: Contact;
  accounts: CrmAccount[];
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function EditContactForm({ contact, accounts, onCancel, onSaved }: EditContactFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  // Mirror the create form fields. Status flips happen via the per-row
  // Deactivate / Reactivate buttons in the list — not as a dropdown in
  // the edit form — so the UI matches the action surface for items /
  // parties / warehouses. ownerUserId and notes aren't surfaced here
  // (create form doesn't include them either — keeping parity).
  const [form, setForm] = useState<ContactUpdate>({
    firstName: contact.firstName,
    lastName: contact.lastName,
    title: contact.title,
    accountId: contact.accountId,
    email: contact.email,
    phone: contact.phone,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: ContactUpdate = {
        firstName: form.firstName?.trim() || undefined,
        lastName: form.lastName?.trim() || undefined,
        // Empty optional text fields collapse to null so the server clears them.
        title: form.title && form.title.trim() ? form.title.trim() : null,
        accountId: form.accountId ?? null,
        email: form.email && form.email.trim() ? form.email.trim() : null,
        phone: form.phone && form.phone.trim() ? form.phone.trim() : null,
      };
      await api().patch(`/crm/contacts/${contact.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('contacts.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('contacts.edit.title', { name: contact.displayName })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="ce-first" label={t('contacts.create.firstName')} required>
          <Input
            id="ce-first"
            value={form.firstName ?? ''}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="ce-last" label={t('contacts.create.lastName')} required>
          <Input
            id="ce-last"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            required
          />
        </FormField>
        <FormField id="ce-title" label={t('contacts.create.jobTitle')}>
          <Input
            id="ce-title"
            value={form.title ?? ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </FormField>
        <FormField id="ce-account" label={t('contacts.create.account')}>
          <select
            id="ce-account"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.accountId ?? ''}
            onChange={(e) => setForm({ ...form, accountId: e.target.value || null })}
          >
            <option value="">{t('contacts.create.noAccount')}</option>
            {accounts.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
          </select>
        </FormField>
        <FormField id="ce-email" label={t('contacts.create.email')}>
          <Input
            id="ce-email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </FormField>
        <FormField id="ce-phone" label={t('contacts.create.phone')}>
          <PhoneInput id="ce-phone" value={form.phone ?? ''} onChange={(v) => setForm({ ...form, phone: v })} />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('contacts.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{t('contacts.edit.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
